import { NodeContentSizing } from '@/enums/node-content-sizing.enum';
import { NodeType } from '@/enums/node-type.enum';
import { CORNER_AND_VERTICAL_RESIZE, ResizeType, type ResizeTypeInput } from '@/enums/resize-type.enum';
import type { NodeType as GraphNodeType } from 'web';

export type ResizableNodeType = GraphNodeType | 'stickynote' | 'custom_group' | 'rw_aleph2_keyframe';

type NodeResizePolicy = {
  resize: ResizeTypeInput;
  contentSizing?: NodeContentSizing;
};

export const NON_RESIZABLE_NODE_TYPES: readonly ResizableNodeType[] = [
  NodeType.StickyNote,
  NodeType.WorkflowOutput,
  'router',
  NodeType.CustomGroup,
  NodeType.Boolean,
  NodeType.Integer,
  'number_selector',
  NodeType.Export,
  'seed',
  NodeType.MultiLora,
  'deprecated',
];

export const CORNER_AND_EDGE_RESIZABLE_NODE_TYPES: readonly ResizableNodeType[] = [
  NodeType.String,
  NodeType.PromptConcat,
  NodeType.PromptV3,
];

export const FILL_FIRST_CHILD_NODE_TYPES: readonly ResizableNodeType[] = [
  'color_correction',
  'custom_glsl',
  'levels',
  'comparison',
  'compv3',
  'extract_video_frame',
  'masks',
  'painterV2',
  NodeType.FigmaNode,
];

export const MEDIA_CONTENT_SIZING_NODE_TYPES: readonly ResizableNodeType[] = [
  'point_cloud_from_depth',
  'kling_element',
  'keyframe',
  'rw_aleph2_keyframe',
];

export const THREE_D_VIEWER_RESIZE_SCALER_NODE_TYPES: readonly ResizableNodeType[] = ['point_cloud_from_depth'];

const NON_RESIZABLE_LOOKUP = new Set<string>(NON_RESIZABLE_NODE_TYPES);
const CORNER_AND_EDGE_LOOKUP = new Set<string>(CORNER_AND_EDGE_RESIZABLE_NODE_TYPES);
const FILL_FIRST_CHILD_LOOKUP = new Set<string>(FILL_FIRST_CHILD_NODE_TYPES);
const MEDIA_CONTENT_SIZING_LOOKUP = new Set<string>(MEDIA_CONTENT_SIZING_NODE_TYPES);
const THREE_D_VIEWER_RESIZE_SCALER_LOOKUP = new Set<string>(THREE_D_VIEWER_RESIZE_SCALER_NODE_TYPES);

const NON_RESIZABLE_POLICY: NodeResizePolicy = { resize: ResizeType.None };
const CORNER_AND_EDGE_POLICY: NodeResizePolicy = { resize: CORNER_AND_VERTICAL_RESIZE };
const DEFAULT_NODE_RESIZE_POLICY: NodeResizePolicy = { resize: ResizeType.Diagonal };

const resizePolicyForNodeType = (nodeType: string | undefined): NodeResizePolicy => {
  if (nodeType === undefined) return DEFAULT_NODE_RESIZE_POLICY;
  if (NON_RESIZABLE_LOOKUP.has(nodeType)) return NON_RESIZABLE_POLICY;
  if (CORNER_AND_EDGE_LOOKUP.has(nodeType)) return CORNER_AND_EDGE_POLICY;
  return DEFAULT_NODE_RESIZE_POLICY;
};

const contentSizingForNodeType = (nodeType: string | undefined): NodeContentSizing | undefined =>
  nodeType !== undefined && MEDIA_CONTENT_SIZING_LOOKUP.has(nodeType) ? NodeContentSizing.Media : undefined;

const policyForNodeType = (nodeType: string | undefined): NodeResizePolicy => {
  const policy = resizePolicyForNodeType(nodeType);
  const contentSizing = contentSizingForNodeType(nodeType);
  return contentSizing === undefined ? policy : { ...policy, contentSizing };
};

export const resolveNodeResizePolicy = (nodeType: string | undefined, override?: ResizeTypeInput): NodeResizePolicy => {
  const policy = policyForNodeType(nodeType);
  return override === undefined ? policy : { ...policy, resize: override };
};

export const shouldFillFirstChild = (nodeType: string | undefined): boolean =>
  nodeType !== undefined && FILL_FIRST_CHILD_LOOKUP.has(nodeType);

export const shouldMeasureThreeDViewerOnResize = (nodeType: string | undefined): boolean =>
  nodeType !== undefined && THREE_D_VIEWER_RESIZE_SCALER_LOOKUP.has(nodeType);
